import { BaseRequest } from '@trukt/types/BaseRequest';
import { Commodity } from '@trukt/types/Commodity';
import { ISODate } from '@trukt/types/ISODate';
import { PaginatedQueryParams } from '@trukt/types/PaginatedQueryParams';
import { SimplePaginatedResults } from '@trukt/types/PaginatedResults';
import { AxiosInstance } from 'axios';

export type StopRequestParams = { guid: string };
export type StopUpdateRequestBody = {
  notes?: string | null;
  dateRequestedStart?: ISODate;
  dateRequestedEnd?: ISODate;
  dateScheduledStart?: ISODate;
  dateScheduledEnd?: ISODate;
  terminal?: { guid: string };
  primaryContact?: { guid: string } | null;
  alternativeContact?: { guid: string } | null;
};
export type StopUpdateRequest = BaseRequest<void, StopUpdateRequestBody, StopRequestParams>;

export type StopDetachCommodityRequest = BaseRequest<
  void,
  {
    jobGuid: string;
    orderGuid: string;
    commodities: Pick<Commodity, 'guid'>[];
  },
  StopRequestParams
>;
export type StopDetachCommodityResponse = void;

export type StopStatusUpdateRequest = BaseRequest<
  void,
  {
    commodities: Pick<Commodity, 'guid'>[] | string[];
    datetime: string | null;
    action: string;
  },
  StopRequestParams
>;

export type StopGetDateTypeRequest = BaseRequest<Omit<PaginatedQueryParams, 'order'>, void, void>;
export type StopGetDateTypeResponse = SimplePaginatedResults<{
  id: string;
  label: string;
  value: string;
}>;

export class StopService {
  instance: AxiosInstance;
  constructor(instance: AxiosInstance) {
    this.instance = instance;
  }

  update(request: StopUpdateRequest) {
    return this.instance.patch(`/stops/${request.routeParams.guid}`, request.body);
  }

  attachCommodity({ routeParams, body }: StopDetachCommodityRequest) {
    return this.instance.put<StopDetachCommodityResponse>(
      `stops/${routeParams.guid}/commodities`,
      body,
    );
  }

  detachCommodity({ routeParams, body }: StopDetachCommodityRequest) {
    return this.instance.delete<StopDetachCommodityResponse>(
      `stops/${routeParams.guid}/commodities`,
      {
        data: body,
      },
    );
  }

  updateStopStatus({ routeParams, body }: StopStatusUpdateRequest) {
    return this.instance.put(`/stops/${routeParams.guid}/status`, {
      commodities: body.commodities,
      datetime: body.datetime,
      action: body.action,
    });
  }

  getDateTypes({ queryParams }: StopGetDateTypeRequest) {
    return this.instance.get<StopGetDateTypeResponse>('/stops/date-types', {
      params: queryParams,
    });
  }
}
